import { useEffect, useState } from 'react';
import { Check, Sparkles, X } from 'lucide-react';
import {
  ALI_OPTIONS,
  EAP_OPTIONS,
} from '@/features/campaigns/constants/campaignOptions';

type AudienceFields = {
  economicActivity: string;
  lifestyle: string;
  buyingPowerZone?: string;
};

type AudienceDemographicProps = {
  formData: AudienceFields;
  onChange: (patch: Partial<AudienceFields>) => void;
};

function resolveBuyingPower(eap: string, ali: string) {
  if (!eap && !ali) return '';
  if (
    ['EAP-Elite', 'EAP-Advanced'].includes(eap) ||
    ['ALI-Elite+', 'ALI-Affluent'].includes(ali)
  ) {
    return 'Elite';
  }
  if (
    ['EAP-Growth', 'EAP-Stable'].includes(eap) ||
    ['ALI-Premium', 'ALI-Enhanced'].includes(ali)
  ) {
    return 'Prime';
  }
  if (
    ['EAP-Developing', 'EAP-Emerging'].includes(eap) ||
    ['ALI-Comfort', 'ALI-Connected'].includes(ali)
  ) {
    return 'Urban Growth';
  }
  return 'Mass Market';
}

function parseOptionLabel(label: string) {
  // "EAP—Elite (High professional…)" or "ALI—Premium (…)"
  const dash = label.match(/^[A-Z]+[—–-](.+)$/);
  const body = dash?.[1]?.trim() || label;
  const paren = body.match(/^(.+?)\s*\((.+)\)\s*$/);
  if (paren) {
    return { title: paren[1].trim(), detail: paren[2].trim() };
  }
  return { title: body, detail: '' };
}

function OptionList({
  legend,
  options,
  value,
  onSelect,
}: {
  legend: string;
  options: readonly { key: string; label: string }[];
  value: string;
  onSelect: (key: string) => void;
}) {
  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="mb-2 px-0.5 text-[12px] font-semibold tracking-[-0.01em] text-[var(--dash-ink)]">
        {legend}
      </legend>
      <div
        className="max-h-[220px] space-y-1 overflow-y-auto overscroll-contain pr-0.5"
        role="listbox"
        aria-label={legend}
      >
        {options.map((opt) => {
          const active = value === opt.key;
          const { title, detail } = parseOptionLabel(opt.label);
          return (
            <button
              key={opt.key}
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => onSelect(active ? '' : opt.key)}
              className={`flex w-full items-start gap-2.5 rounded-[12px] px-2.5 py-2 text-left transition active:scale-[0.995] ${
                active
                  ? 'bg-[var(--dash-brand)]/[0.08] ring-1 ring-[var(--dash-brand)]/25'
                  : 'hover:bg-black/[0.035]'
              }`}
            >
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition ${
                  active
                    ? 'bg-[var(--dash-brand)] text-white'
                    : 'border border-[#D5DBE5] bg-white'
                }`}
                aria-hidden
              >
                {active ? <Check className="h-3 w-3 stroke-[3]" /> : null}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold tracking-[-0.015em] text-[var(--dash-ink)]">
                  {title}
                </span>
                {detail ? (
                  <span className="mt-0.5 block text-[11px] leading-snug text-[var(--dash-muted)]">
                    {detail}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export function AudienceDemographic({
  formData,
  onChange,
}: AudienceDemographicProps) {
  const [bpz, setBpz] = useState(
    formData.buyingPowerZone ||
      resolveBuyingPower(formData.economicActivity, formData.lifestyle),
  );

  useEffect(() => {
    const next = resolveBuyingPower(
      formData.economicActivity,
      formData.lifestyle,
    );
    setBpz(next);
    if (next !== (formData.buyingPowerZone || '')) {
      onChange({ buyingPowerZone: next });
    }
    // intentionally omit onChange to avoid parent inline-fn loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.economicActivity, formData.lifestyle]);

  const hasSelection = Boolean(
    formData.economicActivity || formData.lifestyle,
  );

  return (
    <div className="relative z-20 flex flex-col gap-3 text-left">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
        <OptionList
          legend="Economic zone"
          options={EAP_OPTIONS}
          value={formData.economicActivity}
          onSelect={(economicActivity) => onChange({ economicActivity })}
        />
        <OptionList
          legend="Lifestyle level"
          options={ALI_OPTIONS}
          value={formData.lifestyle}
          onSelect={(lifestyle) => onChange({ lifestyle })}
        />
      </div>

      {bpz ? (
        <div className="flex items-center gap-3 rounded-[14px] border border-black/[0.06] bg-gradient-to-r from-[#F7F8FA] to-white px-3 py-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--dash-brand)]/10 text-[var(--dash-brand)]">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--dash-muted)]">
              Estimated buying power
            </p>
            <p className="m-0 text-[14px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]">
              {bpz}
            </p>
          </div>
          {hasSelection ? (
            <button
              type="button"
              onClick={() =>
                onChange({
                  economicActivity: '',
                  lifestyle: '',
                  buyingPowerZone: '',
                })
              }
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-[var(--dash-muted)] transition hover:bg-black/[0.04] hover:text-[var(--dash-ink)]"
            >
              <X className="h-3 w-3" />
              Clear
            </button>
          ) : null}
        </div>
      ) : (
        <p className="m-0 rounded-[12px] bg-[#F2F5F9] px-3 py-2.5 text-[12px] leading-relaxed text-[var(--dash-muted)]">
          Optional — pick a zone and lifestyle to estimate buying power for
          screen recommendations. You can skip this entirely.
        </p>
      )}
    </div>
  );
}
